// React host for the Zoo skin stage: one PixiJS application per mounted stage.
import { useEffect, useRef, type JSX } from "react";
import { ZooPixiStage } from "./stage";
import type { ZooClock, ZooScene } from "./types";

export interface ZooStageProps {
  scene: ZooScene;
  clock: ZooClock;
  /** Calm motion (reduced motion): no particles, minimal motion, gentle idle only. */
  calm: boolean;
  height: number;
  className?: string;
  /** Accessible name of the canvas (role="img"). */
  label: string;
}

export function ZooStage(props: ZooStageProps): JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<ZooPixiStage | null>(null);
  const latest = useRef(props);
  latest.current = props;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const p = latest.current;
    const stage = new ZooPixiStage(p.scene, p.clock, p.calm);
    let cancelled = false;
    stage
      .init(host, p.label)
      .then(() => {
        // StrictMode / fast unmount: the effect was cleaned up while Pixi was initialising.
        if (cancelled) {
          stage.destroy();
          return;
        }
        stageRef.current = stage;
        // Props may have changed while initialising.
        const q = latest.current;
        stage.setScene(q.scene);
        stage.setClock(q.clock);
        stage.setCalm(q.calm);
        stage.setLabel(q.label);
      })
      .catch((err: unknown) => {
        console.warn("Zoo stage could not start", err);
      });
    return () => {
      cancelled = true;
      if (stageRef.current === stage) {
        stageRef.current = null;
        stage.destroy();
      }
    };
  }, []);

  useEffect(() => {
    stageRef.current?.setScene(props.scene);
  }, [props.scene]);
  useEffect(() => {
    stageRef.current?.setClock(props.clock);
  }, [props.clock]);
  useEffect(() => {
    stageRef.current?.setCalm(props.calm);
  }, [props.calm]);
  useEffect(() => {
    stageRef.current?.setLabel(props.label);
  }, [props.label]);

  return (
    <div
      ref={hostRef}
      className={props.className}
      style={{ position: "relative", width: "100%", height: props.height, overflow: "hidden" }}
    />
  );
}
